import { AfterViewInit, Component, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import {
  CloneIssueDestinationStep,
  CloneIssueModalContext,
  CloneIssueModalService
} from 'src/app/services/clone-issue-modal.service';

@Component({
  selector: 'clone-issue-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './clone-issue-modal.component.html',
  styleUrl: './clone-issue-modal.component.scss'
})
export class CloneIssueModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild('content') private content!: TemplateRef<unknown>;

  ctx: CloneIssueModalContext | null = null;

  selectedStepIds = new Set<number>();

  private modalRef?: NgbModalRef;
  private readonly subscription = new Subscription();

  constructor(
    private cloneIssueModalService: CloneIssueModalService,
    private modalService: NgbModal
  ) { }

  ngAfterViewInit(): void {
    this.subscription.add(
      this.cloneIssueModalService.state$.subscribe(state => {
        if (state.isOpen && state.context) {
          const isFirstOpen = !this.ctx;
          this.ctx = state.context;
          if (isFirstOpen) {
            this.resetSelection();
          }
          this.openModal();
        } else {
          this.closeModal();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.closeModal();
  }

  private openModal(): void {
    if (this.modalRef) {
      return;
    }

    this.modalRef = this.modalService.open(this.content, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      windowClass: 'clone-issue-modal'
    });

    this.modalRef.result.catch(() => {
      this.modalRef = undefined;
      const onClose = this.ctx?.onClose;
      this.ctx = null;
      this.resetSelection();
      onClose?.();
      this.cloneIssueModalService.close();
    });
  }

  private closeModal(): void {
    this.ctx = null;
    this.resetSelection();
    const ref = this.modalRef;
    this.modalRef = undefined;
    ref?.dismiss();
  }

  private resetSelection(): void {
    this.selectedStepIds = new Set<number>();
  }

  close(): void {
    this.ctx?.onClose();
  }

  confirm(): void {
    if (!this.isValid() || this.ctx?.isSaving) {
      return;
    }
    this.ctx?.onConfirm([...this.selectedStepIds]);
  }


  get steps(): CloneIssueDestinationStep[] {
    return this.ctx?.steps ?? [];
  }

  isStepSelected(step: CloneIssueDestinationStep): boolean {
    return this.selectedStepIds.has(step.projectTestlistStepId);
  }

  onStepToggle(step: CloneIssueDestinationStep, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const next = new Set(this.selectedStepIds);
    if (checked) {
      next.add(step.projectTestlistStepId);
    } else {
      next.delete(step.projectTestlistStepId);
    }
    this.selectedStepIds = next;
  }

  areAllSelected(): boolean {
    const steps = this.steps;
    return steps.length > 0 && steps.every(step => this.isStepSelected(step));
  }

  areSomeSelected(): boolean {
    return this.selectedCount > 0 && !this.areAllSelected();
  }

  onToggleAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedStepIds = checked
      ? new Set(this.steps.map(step => step.projectTestlistStepId))
      : new Set<number>();
  }

  get selectedCount(): number {
    return this.selectedStepIds.size;
  }

  isValid(): boolean {
    return this.selectedCount > 0;
  }
}
